import {
  Suspense,
  lazy,
  useCallback,
  useDeferredValue,
  useEffect,
  useState,
} from 'react'
import contentTypeUtils from '../contentTypeUtils.js'
import integrationUtils from '../integrationUtils.js'
import studioDashboardUtils from '../studioDashboardUtils.js'
import {
  buildCaptureRoutingPayload,
  buildConvertedCapturePatch,
  buildDeliveryReceipt,
  buildPublishChecklist,
  buildSourceWorkMetadata,
  normalizeEntityTruthLinks,
} from '../studioWorkflowUtils.js'
import { S, btn } from './uiStyles.js'
import { Toast, PersistentBanner } from './uiComponents.jsx'
import { AppShell } from './uiShell.jsx'
import { Storage } from './studioStorage.js'
import { NAV, NAV_GROUPS, TOPBAR, daysSince } from './studioConstants.js'
import {
  apiBootstrapStudio,
  apiFetchArchivedPosts,
  apiSavePost,
  apiUpdatePost,
  apiDeletePost,
  apiSaveIdea,
  apiDeleteIdea,
  apiUpdateIdea,
  apiUpdateResearchBrief,
  apiUpdateStartDate,
  apiFetchGithubSettings,
  apiUpdateGithubSettings,
  apiRunAuthorityReview,
  apiFetchAuthorityInbox,
  apiUpdateAuthorityTopicCandidate,
  apiConvertAuthorityTopicCandidate,
  apiImportLocalSnapshot,
  apiFetchReports,
  apiFetchPublishAttempts,
  apiFetchRuntimeVerifications,
  apiRunRuntimeVerification,
  apiJson,
  apiSaveProject,
  apiUpdateProject,
  apiSavePosition,
  apiUpdatePosition,
  apiApproveTruthSuggestion,
  apiBatchResolveTruthSuggestions,
  apiDismissTruthSuggestion,
  apiFetchTruthReviewQueue,
  apiInferTruthSuggestions,
  apiUpdateProofAsset,
  apiReindexProofAssets,
  apiCreateTopicFromProofAsset,
  apiLancerEngagementQueue,
} from './studioApi.js'
import { LoginGate } from './LoginGate.jsx'
import { DashboardTab } from './DashboardTab.jsx'
import { Spinner } from './uiComponents.jsx'
import { useAuth } from './contexts/AuthContext.jsx'
import { useIntegrationHealth } from './contexts/IntegrationHealthContext.jsx'
import { useStudioState } from './contexts/StudioStateContext.jsx'
import { usePillars } from './contexts/PillarsContext'
import { useTruth } from './contexts/TruthContext.jsx'
import { useAuthority } from './contexts/AuthorityContext.jsx'
import { useProof } from './contexts/ProofContext.jsx'
import { usePostHandlers } from './hooks/usePostHandlers.js'
import { useIdeaHandlers } from './hooks/useIdeaHandlers.js'
import { useTruthHandlers } from './hooks/useTruthHandlers.js'
import { useAuthorityHandlers } from './hooks/useAuthorityHandlers.js'
import { useProofHandlers } from './hooks/useProofHandlers.js'
import { useResearchHandlers } from './hooks/useResearchHandlers.js'
import { useBiasOverrideBanner } from './hooks/useBiasOverrideBanner.js'

// Non-landing tabs are lazy-loaded so the initial bundle stays close to the
// Dashboard payload. Each `lazy(() => import('./X.jsx'))` produces its own
// chunk. A single Suspense boundary wraps the tab outlet below.
//
// Wave 47o — lazyWithReload wraps every dynamic import to recover from
// stale chunk references after a Railway redeploy. When the build
// publishes new asset hashes, an open browser still holds the old
// `index.js` which references the old chunk hash; clicking a tab fires
// `import('./AutonomyTab.jsx')`, the network 404s on the now-deleted
// hash, and React surfaces a "Failed to fetch dynamically imported
// module" error to the boundary. The fix: detect that specific error
// shape, set a one-shot sessionStorage guard so we don't infinite-loop
// on a genuinely broken deploy, and trigger window.location.reload()
// so the browser picks up the fresh `index.js` + the new chunk hashes.
const STALE_CHUNK_KEY = 'studio.stale-chunk-reload'
const STALE_CHUNK_PATTERN =
  /Failed to fetch dynamically imported module|Loading chunk \d+ failed|ChunkLoadError|Importing a module script failed/i

function lazyWithReload(loader) {
  return lazy(() =>
    loader()
      .then((mod) => {
        // Successful import — clear the guard so a future stale error
        // is allowed to trigger one fresh reload attempt.
        try {
          window.sessionStorage.removeItem(STALE_CHUNK_KEY)
        } catch {
          // sessionStorage can throw in strict-private contexts. Ignore.
        }
        return mod
      })
      .catch((err) => {
        const message = err?.message || String(err || '')
        if (!STALE_CHUNK_PATTERN.test(message)) throw err
        try {
          if (window.sessionStorage.getItem(STALE_CHUNK_KEY) === '1') {
            // Already reloaded once; the deploy itself may be broken.
            // Surface the error so the operator sees the boundary fallback.
            throw err
          }
          window.sessionStorage.setItem(STALE_CHUNK_KEY, '1')
        } catch (storageErr) {
          // If sessionStorage is unavailable we can't gate the reload
          // — bail out to the boundary rather than risk an infinite loop.
          throw err
        }
        window.location.reload()
        // Block the lazy() promise so React keeps showing Suspense
        // fallback while the page reloads.
        return new Promise(() => {})
      })
  )
}

const CreateTab = lazyWithReload(() =>
  import('./CreateTab.jsx').then((m) => ({ default: m.CreateTab }))
)
const IdeasTab = lazyWithReload(() =>
  import('./IdeasTab.jsx').then((m) => ({ default: m.IdeasTab }))
)
const ResearchTab = lazyWithReload(() =>
  import('./ResearchTab.jsx').then((m) => ({ default: m.ResearchTab }))
)
const TruthTab = lazyWithReload(() =>
  import('./TruthTab.jsx').then((m) => ({ default: m.TruthTab }))
)
const PostsTab = lazyWithReload(() =>
  import('./PostsTab.jsx').then((m) => ({ default: m.PostsTab }))
)
const InsightsTab = lazyWithReload(() =>
  import('./InsightsTab.jsx').then((m) => ({ default: m.InsightsTab }))
)
const LancerTab = lazyWithReload(() =>
  import('./LancerTab.jsx').then((m) => ({ default: m.LancerTab }))
)
const EngagementTab = lazyWithReload(() =>
  import('./EngagementTab.jsx').then((m) => ({ default: m.EngagementTab }))
)
const CalendarTab = lazyWithReload(() =>
  import('./CalendarTab.jsx').then((m) => ({ default: m.CalendarTab }))
)
const ProgressTab = lazyWithReload(() =>
  import('./ProgressTab.jsx').then((m) => ({ default: m.ProgressTab }))
)
const ReportTab = lazyWithReload(() =>
  import('./ReportTab.jsx').then((m) => ({ default: m.ReportTab }))
)
const ProofTab = lazyWithReload(() =>
  import('./ProofTab.jsx').then((m) => ({ default: m.ProofTab }))
)
const ArchiveTab = lazyWithReload(() =>
  import('./ArchiveTab.jsx').then((m) => ({ default: m.ArchiveTab }))
)
const SettingsTab = lazyWithReload(() =>
  import('./SettingsTab.jsx').then((m) => ({ default: m.SettingsTab }))
)
const AutonomyTab = lazyWithReload(() =>
  import('./AutonomyTab.jsx').then((m) => ({ default: m.AutonomyTab }))
)

const {
  normalizePost,
  normalizeIdea,
  getContentType,
  createStudioSnapshot,
  normalizeStudioSnapshot,
} = contentTypeUtils

const { FEATURE_FLAG_DEFAULTS } = integrationUtils
const { buildDashboardState, buildNavCounts } = studioDashboardUtils

const DEFAULT_INTEGRATION_HEALTH = {
  flags: {
    autonomyWorkerEnabled: FEATURE_FLAG_DEFAULTS.AUTONOMY_WORKER_ENABLED,
    enableLivePosting: FEATURE_FLAG_DEFAULTS.ENABLE_LIVE_POSTING,
    enableNotionSync: FEATURE_FLAG_DEFAULTS.ENABLE_NOTION_SYNC,
    enableLinkedinArticlePublishing:
      FEATURE_FLAG_DEFAULTS.ENABLE_LINKEDIN_ARTICLE_PUBLISHING,
  },
  integrations: {},
}

function readLegacyLocalSnapshot() {
  const posts = Storage.get('ignazio-posts') || []
  const ideas = Storage.get('ignazio-ideas') || []
  const startDate = Storage.get('start-date') || ''
  if (!posts.length && !ideas.length && !startDate) return null
  return createStudioSnapshot({ posts, ideas, startDate })
}

function normalizeBootstrapError(error) {
  return {
    message:
      error?.message ||
      'The durable studio state did not load. Resolve the backend failure before treating this workspace as current.',
    code: error?.code || 'BOOTSTRAP_FAILED',
  }
}

// ── Root ──────────────────────────────────────────────────────────────────────

export default function App() {
  const [tab, setTab] = useState('lancer')
  // Durable studio entities live in StudioStateContext (Wave 7 Phase A).
  // Aliases below preserve the existing call-site shape so applyBootstrap,
  // resetStudioData, and per-entity handlers can keep using setX directly.
  const studioState = useStudioState()
  const pillarsRegistry = usePillars()
  const {
    posts,
    setPosts,
    archivedPosts,
    setArchivedPosts,
    archivedPostsCount,
    setArchivedPostsCount,
    ideas,
    setIdeas,
    archivedIdeas,
    setArchivedIdeas,
    researchBriefs,
    setResearchBriefs,
    projects,
    setProjects,
    positions,
    setPositions,
    reports,
    setReports,
    publishAttempts,
    setPublishAttempts,
  } = studioState
  // Truth state (suggestions + review queue) lives in TruthContext (Wave 7
  // Phase B). The applyTruthReviewQueue reducer that used to live here is
  // exposed by the context as `applyReviewQueue`.
  const truthCtx = useTruth()
  const truthSuggestions = truthCtx.suggestions
  const setTruthSuggestions = truthCtx.setSuggestions
  const truthReviewQueue = truthCtx.reviewQueue
  // Authority state (reviews, topic candidates, coverage, current review,
  // review loading) lives in AuthorityContext (Wave 7 Phase C).
  const authorityCtx = useAuthority()
  const authorityReviews = authorityCtx.reviews
  const setAuthorityReviews = authorityCtx.setReviews
  const authorityTopicCandidates = authorityCtx.topicCandidates
  const setAuthorityTopicCandidates = authorityCtx.setTopicCandidates
  const authorityCoverage = authorityCtx.coverage
  const setAuthorityCoverage = authorityCtx.setCoverage
  const authorityReview = authorityCtx.currentReview
  const setAuthorityReview = authorityCtx.setCurrentReview
  const authorityReviewLoading = authorityCtx.reviewLoading
  const setAuthorityReviewLoading = authorityCtx.setReviewLoading
  // Proof state lives in ProofContext (Wave 7 Phase D).
  const proofCtx = useProof()
  const strategicAssets = proofCtx.assets
  const setStrategicAssets = proofCtx.setAssets
  const strategicAssetCoverage = proofCtx.coverage
  const setStrategicAssetCoverage = proofCtx.setCoverage
  // reports + publishAttempts moved to StudioStateContext (Wave 7 Phase A).
  const [focusedPostId, setFocusedPostId] = useState('')
  // Engagement action count (to-send drafts + inbound) for the nav badge.
  // Polled app-wide so the operator sees "N waiting" without opening the tab.
  const [engagementCount, setEngagementCount] = useState(0)
  const [startDate, setStartDate] = useState('')
  const [githubSettings, setGithubSettings] = useState({ repos: [] })
  const [migrationState, setMigrationState] = useState({
    completed: false,
    hasDurableData: false,
    completedAt: null,
  })
  const [legacySnapshot, setLegacySnapshot] = useState(() =>
    readLegacyLocalSnapshot()
  )
  const [studioLoading, setStudioLoading] = useState(true)
  const [bootstrapError, setBootstrapError] = useState(null)
  // integrationHealth + runtimeVerifications live in IntegrationHealthContext
  // (provided by main.jsx). App.jsx pushes bootstrap data into the context
  // via setHealth / setVerifications and reads via context.health /
  // context.verifications. Local aliases below keep call sites short.
  const integrationHealthCtx = useIntegrationHealth()
  const integrationHealth = integrationHealthCtx.health
  const setIntegrationHealth = integrationHealthCtx.setHealth
  const runtimeVerifications = integrationHealthCtx.verifications
  const setRuntimeVerifications = integrationHealthCtx.setVerifications
  const [toast, setToast] = useState('')
  const [prefillTopic, setPrefillTopic] = useState('')
  const [prefillCtx, setPrefillCtx] = useState('')
  const [prefillPillar, setPrefillPillar] = useState('')
  const [prefillContentType, setPrefillContentType] = useState('')
  const [prefillMode, setPrefillMode] = useState('')
  const [prefillSourceWork, setPrefillSourceWork] = useState(null)
  const [prefillTruthLinks, setPrefillTruthLinks] = useState(null)
  const [prefillDraftId, setPrefillDraftId] = useState('')
  const [prefillComposeContext, setPrefillComposeContext] = useState(null)
  const [commandQuery, setCommandQuery] = useState('')
  const deferredCommandQuery = useDeferredValue(commandQuery)
  const [commandOpen, setCommandOpen] = useState(false)
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth
  )
  const [railOpen, setRailOpen] = useState(false)
  const [bannerMessages, setBannerMessages] = useState([])
  // Bias override expiry surface (Wave 14) lives in useBiasOverrideBanner —
  // see the hook call below, after auth state is available.
  // Auth state lives in AuthContext (provided by main.jsx). The provider
  // auto-fetches /api/auth/status on mount; App reacts to its loading +
  // authenticated values to decide whether to gate the app or load the
  // studio bootstrap. Login / logout flow through the context too.
  const auth = useAuth()
  const authState = {
    loading: auth.loading,
    authEnabled: auth.authEnabled,
    authenticated: auth.authenticated,
    user: auth.user,
  }
  const [authError, setAuthError] = useState('')

  // Bias-override expiry banner (state, 60s poll, localStorage dismissal,
  // quick-extend) — extracted to a hook (P2 item 12). authState +
  // setToast are declared above, so the hook's deps are satisfied here.
  const biasBanner = useBiasOverrideBanner({
    authenticated: authState.authenticated,
    setToast,
  })

  function addBanner(id, text, tone, dismissable = true) {
    setBannerMessages((prev) => {
      if (prev.some((m) => m.id === id)) return prev
      return [...prev, { id, text, tone, dismissable }]
    })
  }

  function dismissBanner(id) {
    // Bias-override banners persist their dismissal across reloads so
    // the operator isn't re-prompted for the same expired override. The
    // banner-id format (`bias-override-*:<overrideId>`) is owned here; the
    // hook records the dismissal.
    if (typeof id === 'string' && id.startsWith('bias-override-')) {
      const overrideId = id.split(':')[1]
      if (overrideId) biasBanner.dismissOverride(overrideId)
      return
    }
    setBannerMessages((prev) => prev.filter((m) => m.id !== id))
  }

  // Wave 12 — post handlers extracted to a custom hook so App.jsx is
  // closer to a pure orchestrator. The hook reads StudioState +
  // IntegrationHealth contexts directly; we only pass the UI callbacks
  // (toast / banner) since they live above the studio state layer.
  const {
    notionPush,
    notionUpdateStatus,
    substackPush,
    handleSave,
    handleDelete,
    handleMarkPublished,
    handleUpdatePost,
    handleEdit,
    handleRetrySync,
    handleDuplicate,
    handleRestorePost,
  } = usePostHandlers({ onToast: setToast, addBanner })

  const {
    handleSaveIdea,
    handleUpdateIdea,
    handleDeleteIdea,
    handleRestoreIdea,
  } = useIdeaHandlers()

  const applyBootstrap = useCallback(
    (payload) => {
      setPosts((payload.posts || []).map(normalizePost))
      // Perf 2026-06-10 — archived posts arrive OFF the blocking bootstrap.
      // Bootstrap ships archivedPosts: [] + an archivedPostsCount; the full
      // list streams in via a deferred fetch (loadStudioData) after first
      // paint. Only overwrite archivedPosts when the payload actually carries
      // rows, so a bootstrap refresh (which sends []) never wipes an already
      // background-loaded archive out from under the Archive tab / backup.
      if (
        Array.isArray(payload.archivedPosts) &&
        payload.archivedPosts.length
      ) {
        setArchivedPosts(payload.archivedPosts.map(normalizePost))
      }
      if (typeof payload.archivedPostsCount === 'number') {
        setArchivedPostsCount(payload.archivedPostsCount)
      }
      setIdeas((payload.ideas || []).map(normalizeIdea))
      setArchivedIdeas((payload.archivedIdeas || []).map(normalizeIdea))
      setResearchBriefs(payload.researchBriefs || [])
      setProjects(payload.projects || [])
      setPositions(payload.positions || [])
      setTruthSuggestions(payload.truthSuggestions || [])
      setAuthorityReviews(payload.authorityReviews || [])
      setAuthorityTopicCandidates(payload.authorityTopicCandidates || [])
      setAuthorityCoverage(payload.authorityCoverage || null)
      setStrategicAssets(payload.strategicAssets || [])
      setStrategicAssetCoverage(payload.strategicAssetCoverage || null)
      setReports(payload.reports || [])
      setPublishAttempts(payload.publishAttempts || [])
      setStartDate(payload.settings?.startDate || '')
      setGithubSettings(payload.settings?.github || { repos: [] })
      setMigrationState(
        payload.migration || {
          completed: false,
          hasDurableData: false,
          completedAt: null,
        }
      )
      setIntegrationHealth(
        payload.integrationHealth || {
          ...DEFAULT_INTEGRATION_HEALTH,
        }
      )
    },
    [
      setIntegrationHealth,
      setPosts,
      setArchivedPosts,
      setArchivedPostsCount,
      setIdeas,
      setArchivedIdeas,
      setResearchBriefs,
      setProjects,
      setPositions,
      setReports,
      setPublishAttempts,
      setTruthSuggestions,
      setAuthorityReviews,
      setAuthorityTopicCandidates,
      setAuthorityCoverage,
      setStrategicAssets,
      setStrategicAssetCoverage,
    ]
  )

  const applyTruthReviewQueue = truthCtx.applyReviewQueue

  const applyAuthorityInbox = authorityCtx.applyInbox

  // Wave 12 Phase C — truth handlers consume StudioState + Truth contexts
  // and need applyBootstrap as a callback (cross-context reducer).
  const {
    handleSaveProject,
    handleUpdateTruthProject,
    handleSavePosition,
    handleUpdateTruthPosition,
    handleApproveTruthSuggestion,
    handleBatchResolveTruthSuggestions,
    handleDismissTruthSuggestion,
    handleInferTruthSuggestions,
  } = useTruthHandlers({ applyBootstrap, onToast: setToast })

  // Wave 12 Phase D — authority data handlers. handleConvertAuthority-
  // TopicCandidate also drives prefill UI + a tab switch, which the hook
  // intentionally leaves out. The wrapper below applies the UI effects
  // after the hook returns the API response.
  const {
    handleAuthorityReview,
    handleUpdateAuthorityTopicCandidate,
    handleConvertAuthorityTopicCandidate: convertAuthorityTopicCandidateData,
  } = useAuthorityHandlers({ applyBootstrap })

  // Wave 12 Phase E — proof handlers.
  const {
    refreshProofAssets,
    handleUpdateProofAsset,
    handleCreateTopicFromProofAsset,
  } = useProofHandlers({ onToast: setToast })

  // Wave 13 — research handlers. captureResearchBrief returns the
  // normalized idea; the App-level wrapper below adds the tab switch
  // + toast (UI orchestration stays out of the data hook).
  const { handleSaveBrief, handleUpdateBrief, captureResearchBrief } =
    useResearchHandlers()

  async function refreshRuntimeVerifications() {
    const verifications = await apiFetchRuntimeVerifications(40)
    setRuntimeVerifications(verifications)
    return verifications
  }

  function resetStudioData() {
    setBootstrapError(null)
    setPosts([])
    setArchivedPosts([])
    setIdeas([])
    setArchivedIdeas([])
    setResearchBriefs([])
    setProjects([])
    setPositions([])
    truthCtx.setReviewQueue({ groups: [], coverage: null, suggestions: [] })
    setTruthSuggestions([])
    setAuthorityReviews([])
    setAuthorityTopicCandidates([])
    setAuthorityCoverage(null)
    setStrategicAssets([])
    setStrategicAssetCoverage(null)
    setReports([])
    setPublishAttempts([])
    setRuntimeVerifications([])
    setStartDate('')
    setGithubSettings({ repos: [] })
    setAuthorityReview(null)
    setMigrationState({
      completed: false,
      hasDurableData: false,
      completedAt: null,
    })
    setIntegrationHealth(DEFAULT_INTEGRATION_HEALTH)
  }

  const loadStudioData = useCallback(async () => {
    setStudioLoading(true)
    setBootstrapError(null)

    let bootstrap
    try {
      bootstrap = await apiBootstrapStudio()
      applyBootstrap(bootstrap)
    } catch (error) {
      setBootstrapError(normalizeBootstrapError(error))
      setStudioLoading(false)
      return
    }

    // Perf 2026-06-10 — paint as soon as the slim (active-only) bootstrap
    // lands. Archived posts (~14MB on prod) and the four secondary inbox /
    // verification calls used to sit inside the awaited block + a finally, so
    // the whole-app spinner waited for the slowest of them (the chief cause of
    // multi-minute loads). They now hydrate in the background, after paint.
    setStudioLoading(false)

    // Deferred background fill of the archive, off the critical path. Archive
    // tab, backup export, and dashboard/settings totals consume archivedPosts;
    // until it lands, archivedPostsCount (from bootstrap) keeps totals correct.
    if (bootstrap?.archivedPostsCount > 0) {
      apiFetchArchivedPosts()
        .then((archived) => setArchivedPosts(archived.map(normalizePost)))
        .catch(() => {})
    }

    const [
      verificationResult,
      githubResult,
      truthReviewQueueResult,
      authorityInboxResult,
    ] = await Promise.allSettled([
      apiFetchRuntimeVerifications(40),
      apiFetchGithubSettings(),
      apiFetchTruthReviewQueue(),
      apiFetchAuthorityInbox(),
    ])

    if (verificationResult.status === 'fulfilled') {
      setRuntimeVerifications(verificationResult.value)
    }
    if (githubResult.status === 'fulfilled') {
      setGithubSettings(githubResult.value)
    }
    if (truthReviewQueueResult.status === 'fulfilled') {
      applyTruthReviewQueue(truthReviewQueueResult.value)
    }
    if (authorityInboxResult.status === 'fulfilled') {
      applyAuthorityInbox(authorityInboxResult.value)
    }
  }, [
    applyAuthorityInbox,
    applyBootstrap,
    applyTruthReviewQueue,
    setRuntimeVerifications,
    setArchivedPosts,
  ])

  // React to AuthContext: once it has resolved (loading=false), load the
  // studio bootstrap if the user is authenticated, otherwise stop the
  // studio loading spinner so the LoginGate renders.
  useEffect(() => {
    if (auth.loading) return
    if (auth.error) {
      setStudioLoading(false)
      setAuthError('Access check failed. Confirm the proxy is running.')
      return
    }
    if (auth.authenticated) {
      loadStudioData()
    } else {
      setStudioLoading(false)
    }
  }, [auth.loading, auth.authenticated, auth.error, loadStudioData])

  useEffect(() => {
    function handleResize() {
      setViewportWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (viewportWidth >= 900) setRailOpen(false)
  }, [viewportWidth])

  // App-wide engagement badge — poll the action count (to-send drafts +
  // inbound) so the operator always sees what's waiting without opening
  // the tab. Best-effort: errors leave the prior count untouched.
  useEffect(() => {
    if (!auth.authenticated) return undefined
    let cancelled = false
    const fetchCount = async () => {
      try {
        const res = await apiLancerEngagementQueue()
        if (cancelled) return
        const c = res?.counts || {}
        setEngagementCount((Number(c.toSend) || 0) + (Number(c.inbound) || 0))
      } catch {
        /* badge is best-effort — keep the last known count */
      }
    }
    fetchCount()
    const id = setInterval(fetchCount, 60000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [auth.authenticated])

  useEffect(() => {
    function handleKeydown(event) {
      if (
        authState.authenticated &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'k'
      ) {
        event.preventDefault()
        setCommandOpen((open) => !open)
      }
      // Wave A — Cmd+L summon for Lancer. Mnemonic, no macOS conflict.
      // Doesn't toggle (always lands on Lancer); operator's escape route
      // is the sidebar. Skip when an input is focused so Cmd+L still
      // works as the OS-level "select address bar" inside text fields.
      if (
        authState.authenticated &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'l' &&
        !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)
      ) {
        event.preventDefault()
        setTab('lancer')
      }
      if (event.key === 'Escape') {
        setCommandOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeydown)
    return () => window.removeEventListener('keydown', handleKeydown)
  }, [authState.authenticated])

  useEffect(() => {
    if (!commandOpen) {
      setCommandQuery('')
    }
  }, [commandOpen])

  // Bias-override expiry polling + localStorage dismissal + GC moved to
  // useBiasOverrideBanner (P2 item 12).

  // Post handlers (notionPush, notionUpdateStatus, handleSave, handleDelete,
  // handleMarkPublished, handleUpdatePost, handleEdit, handleRetrySync,
  // handleDuplicate, handleRestorePost) moved to usePostHandlers above.

  // Truth handlers (handleSaveProject, handleUpdateTruthProject,
  // handleSavePosition, handleUpdateTruthPosition,
  // handleApproveTruthSuggestion, handleBatchResolveTruthSuggestions,
  // handleDismissTruthSuggestion, handleInferTruthSuggestions) moved to
  // useTruthHandlers above.

  function handleNavigateIdea(idea) {
    const payload = buildCaptureRoutingPayload(idea)
    const truthLinks = normalizeEntityTruthLinks(idea)
    setPrefillDraftId('')
    setPrefillComposeContext({
      surface: 'capture',
      label: 'Capture',
      openedAt: new Date().toISOString(),
      targetDraftId: '',
    })
    setPrefillTopic(payload.topic)
    setPrefillCtx(payload.ctx)
    setPrefillPillar(payload.pillar)
    setPrefillContentType(payload.contentType)
    setPrefillMode(payload.mode)
    setPrefillSourceWork(payload.sourceWork)
    setPrefillTruthLinks(truthLinks)
    setTab('create')
  }

  function handleOpenDraftInCompose(post, composeContext = null) {
    if (!post?.id) return
    const type = getContentType(post.contentType || post.platform)
    setPrefillDraftId(post.id)
    setPrefillComposeContext({
      surface: composeContext?.surface || 'drafts',
      label: composeContext?.label || 'Drafts',
      openedAt: new Date().toISOString(),
      targetDraftId: post.id,
    })
    setPrefillTopic('')
    setPrefillCtx('')
    setPrefillPillar('')
    setPrefillContentType('')
    setPrefillSourceWork(null)
    setPrefillTruthLinks(null)
    setPrefillMode(type.key === 'linkedin-carousel' ? 'carousel' : 'edit')
    setTab('create')
  }
  async function handleImportStudioData(snapshot) {
    const data = await apiImportLocalSnapshot(snapshot)
    applyBootstrap(data.bootstrap)
  }

  async function handleImportLegacyLocalData() {
    if (!legacySnapshot) return
    const confirmed = window.confirm(
      'Import the browser-local studio data into the durable store? This keeps the browser backup in place and marks the migration complete.'
    )
    if (!confirmed) return
    const data = await apiImportLocalSnapshot(legacySnapshot)
    applyBootstrap(data.bootstrap)
    setLegacySnapshot(null)
    setToast('Local browser data imported')
  }

  async function handleRunRuntimeVerification(payload) {
    try {
      const data = await apiRunRuntimeVerification(payload)
      if (data.bootstrap) applyBootstrap(data.bootstrap)
      await refreshRuntimeVerifications()
      return data.verification
    } catch (error) {
      if (error.bootstrap) applyBootstrap(error.bootstrap)
      await Promise.allSettled([
        apiBootstrapStudio().then((data) => applyBootstrap(data)),
        refreshRuntimeVerifications(),
      ])
      throw error
    }
  }

  // Research data handlers (handleSaveBrief, handleUpdateBrief) moved
  // to useResearchHandlers above. handleCaptureResearchBrief keeps a
  // thin wrapper here so the tab switch + toast stay co-located with
  // the other UI orchestration.
  async function handleCaptureResearchBrief(brief) {
    const normalized = await captureResearchBrief(brief)
    setTab('ideas')
    setToast('Research brief captured')
    return normalized
  }

  function handleGithubCapturesImported(captures = []) {
    const normalizedCaptures = captures.map(normalizeIdea)
    setIdeas((prev) => [
      ...normalizedCaptures,
      ...prev.filter(
        (item) => !normalizedCaptures.some((capture) => capture.id === item.id)
      ),
    ])
  }

  function handleReportCreated(report) {
    apiFetchReports()
      .then(setReports)
      .catch(() =>
        setReports((prev) => [
          report,
          ...prev.filter((item) => item.id !== report.id),
        ])
      )
    apiReindexProofAssets()
      .then((data) => {
        setStrategicAssets(data.assets || [])
        setStrategicAssetCoverage(data.coverage || null)
      })
      .catch(() => {})
  }

  async function handleStartDateChange(nextStartDate) {
    const data = await apiUpdateStartDate(nextStartDate)
    setStartDate(data.startDate || '')
  }

  async function handleGithubSettingsChange(repos, repoActivityCutoff = '') {
    const settings = await apiUpdateGithubSettings(repos, repoActivityCutoff)
    setGithubSettings(settings)
    return settings
  }

  async function handleCreateTopicFromProofAssetAndOpenCompose(id) {
    const candidate = await handleCreateTopicFromProofAsset(id)
    if (!candidate?.id) return candidate
    await handleConvertAuthorityTopicCandidate(candidate.id, {
      surface: 'proof',
      label: 'Proof',
    })
    return candidate
  }

  function handleOpenConvertedCapturePost(postId) {
    if (!postId) return
    const post = posts.find((item) => item.id === postId)
    if (!post) {
      setToast('Converted draft not found')
      return
    }
    handleOpenDraftInCompose(post, {
      surface: 'capture',
      label: 'Capture',
    })
  }

  // Authority data handlers (handleAuthorityReview,
  // handleUpdateAuthorityTopicCandidate) moved to useAuthorityHandlers
  // above. handleConvertAuthorityTopicCandidate keeps a thin wrapper
  // here so the prefill state + tab switch stay co-located with the
  // other UI orchestration.
  async function handleConvertAuthorityTopicCandidate(
    id,
    composeContext = null
  ) {
    const data = await convertAuthorityTopicCandidateData(id)
    const prefill = data?.prefill || {}
    setPrefillDraftId('')
    setPrefillComposeContext({
      surface: composeContext?.surface || 'authority',
      label: composeContext?.label || 'Authority',
      openedAt: new Date().toISOString(),
      targetDraftId: '',
    })
    setPrefillTopic(prefill.topic || '')
    setPrefillCtx(prefill.ctx || '')
    setPrefillPillar(prefill.pillar || '')
    setPrefillContentType(prefill.contentType || '')
    setPrefillMode(prefill.mode || '')
    setPrefillSourceWork(prefill.sourceWork || null)
    setPrefillTruthLinks({
      projectIds: prefill.projectIds || [],
      positionIds: prefill.positionIds || [],
    })
    setTab('create')
    setToast('Topic candidate opened in Compose')
    return data
  }

  // Proof handlers (refreshProofAssets, handleUpdateProofAsset,
  // handleCreateTopicFromProofAsset) moved to useProofHandlers above.

  async function handleResetStartDate() {
    await handleStartDateChange('')
  }

  async function handleLogin(password) {
    setAuthError('')
    try {
      await auth.login(password)
      await loadStudioData()
    } catch (error) {
      setAuthError(
        error.code === 'AUTH_INVALID_CREDENTIALS'
          ? 'Access denied. Check the studio key and try again.'
          : error.message || 'Login failed.'
      )
      throw error
    }
  }

  async function handleLogout() {
    await auth.logout()
    resetStudioData()
    setCommandOpen(false)
    setRailOpen(false)
    setAuthError('')
  }

  if (authState.loading || !authState.authenticated) {
    return (
      <LoginGate
        S={S}
        btn={btn}
        authLoading={authState.loading}
        authError={authError}
        onLogin={handleLogin}
      />
    )
  }

  const liTokenWarning = daysSince(Storage.get('li-token-saved')) >= 50
  const navCounts = {
    ...buildNavCounts({
      posts,
      ideas,
      archivedPosts,
      archivedIdeas,
      researchBriefs,
      reports,
      truthSuggestions,
      strategicAssets,
    }),
    engagement: engagementCount,
    // Perf 2026-06-10 — archived posts hydrate post-paint, so archivedPosts is
    // briefly empty; use the bootstrap archivedPostsCount for the nav badge so
    // it's correct immediately (max() so it stays right once the array lands).
    archive:
      Math.max(archivedPostsCount, archivedPosts.length) + archivedIdeas.length,
  }
  const dashboard = buildDashboardState({
    posts,
    ideas,
    projects,
    positions,
    archivedPosts,
    archivedIdeas,
    researchBriefs,
    reports,
    publishAttempts,
    runtimeVerifications,
    integrationHealth,
    authorityTopicCandidates,
    strategicAssets,
    strategicAssetCoverage,
  })
  // UI elevation 2026-06-09: topTitle now syncs dynamically with active tab
  // from NAV registry to fix the desync bug where topbar showed wrong title
  // when switching between tabs. Compute from NAV label, fallback to TOPBAR map.
  const navItem = NAV.find((item) => item.id === tab)
  const navLabel = navItem?.label || 'Studio'
  const [topTitle, topSub] = TOPBAR[tab] || [navLabel, 'Content operations']
  const integrationEntries = Object.values(
    integrationHealth?.integrations || {}
  )
  const readyIntegrations = integrationEntries.filter(
    (item) => item.available
  ).length
  const healthTone =
    integrationEntries.length === 0
      ? 'warning'
      : readyIntegrations === integrationEntries.length
        ? 'healthy'
        : readyIntegrations > 0
          ? 'warning'
          : 'critical'
  const durableStateNote =
    legacySnapshot && !migrationState.completed
      ? 'Legacy local snapshot pending import'
      : migrationState.completed || migrationState.hasDurableData
        ? 'Durable store active'
        : 'Local-first bootstrap'

  const commandItems = [
    ...NAV.map((item) => ({
      id: `nav:${item.id}`,
      label: item.label,
      category: 'Workspace',
      tabId: item.id,
    })),
    {
      id: 'nav:calendar',
      label: 'Calendar',
      category: 'Workspace',
      tabId: 'calendar',
    },
    {
      id: 'nav:progress',
      label: '90-day Arc',
      category: 'Workspace',
      tabId: 'progress',
    },
    ...dashboard.resumeItems.map((item) => ({
      id: `resume:${item.id}`,
      label: item.title,
      meta: item.label,
      category: 'Resume',
      tabId: item.actionTab,
    })),
    ...posts.slice(0, 20).map((post) => ({
      id: `post:${post.id}`,
      label: post.topic,
      meta: getContentType(post.contentType).label,
      category:
        post.status === 'scheduled'
          ? 'Queue'
          : post.status === 'published'
            ? 'Delivered'
            : 'Drafts',
      postStatus: post.status,
      tabId:
        post.status === 'scheduled'
          ? 'queue'
          : post.status === 'published'
            ? 'delivered'
            : 'drafts',
    })),
    ...researchBriefs.slice(0, 10).map((brief) => ({
      id: `brief:${brief.id}`,
      label: brief.topic,
      category: 'Research',
      tabId: 'research',
    })),
    ...reports.slice(0, 10).map((report) => ({
      id: `report:${report.id}`,
      label: report.title,
      category: 'Reports',
      tabId: 'reports',
    })),
    ...strategicAssets.slice(0, 10).map((asset) => ({
      id: `proof:${asset.id}`,
      label: asset.title,
      meta: asset.assetType,
      category: 'Proof',
      tabId: 'proof',
    })),
  ]
  const commandResults = (
    deferredCommandQuery.trim()
      ? commandItems.filter((item) => {
          const haystack = `${item.label} ${item.meta}`.toLowerCase()
          return haystack.includes(deferredCommandQuery.trim().toLowerCase())
        })
      : commandItems
  ).slice(0, 8)
  const isMobile = viewportWidth < 900
  const railCollapsed = viewportWidth >= 900 && viewportWidth < 1180
  const sidebarWidth = railCollapsed ? 76 : 236

  function handleCommandSelect(item) {
    setTab(item.tabId)
    setCommandOpen(false)
    setCommandQuery('')
  }

  const sidebarStyle = {
    ...S.sidebar,
    width: sidebarWidth,
    minWidth: sidebarWidth,
    position: isMobile ? 'fixed' : 'relative',
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 40,
    transform: isMobile ? `translateX(${railOpen ? '0' : '-100%'})` : 'none',
    transition: 'transform 180ms ease, width 180ms ease',
  }

  return (
    <AppShell
      S={S}
      btn={btn}
      nav={NAV}
      navGroups={NAV_GROUPS}
      tab={tab}
      setTab={setTab}
      navCounts={navCounts}
      liTokenWarning={liTokenWarning}
      isMobile={isMobile}
      railCollapsed={railCollapsed}
      railOpen={railOpen}
      setRailOpen={setRailOpen}
      sidebarStyle={sidebarStyle}
      topTitle={topTitle}
      topSub={topSub}
      commandOpen={commandOpen}
      setCommandOpen={setCommandOpen}
      commandQuery={commandQuery}
      setCommandQuery={setCommandQuery}
      commandResults={commandResults}
      onCommandSelect={handleCommandSelect}
      durableStateNote={durableStateNote}
      readyIntegrations={readyIntegrations}
      integrationEntries={integrationEntries}
      healthTone={healthTone}
      archiveCount={navCounts.archive}
      reportCount={reports.length}
      publishAttemptsCount={publishAttempts.length}
      authEnabled={authState.authEnabled}
      authUser={authState.user}
      onLogout={handleLogout}
    >
      <PersistentBanner
        messages={[
          ...(legacySnapshot && !migrationState.completed
            ? [
                {
                  id: 'legacy-migration',
                  text: 'Local-only browser data is waiting to be imported into the durable store. Open Settings to complete the one-time migration.',
                  tone: 'info',
                  dismissable: false,
                },
              ]
            : []),
          ...bannerMessages,
          ...biasBanner.buildMessages(
            (pillarId) => pillarsRegistry.getPillar(pillarId)?.label || pillarId
          ),
          ...(bootstrapError
            ? [
                {
                  id: 'bootstrap-failed',
                  text: 'Durable bootstrap failed. The studio is blocked until the durable state loads.',
                  tone: 'error',
                  dismissable: false,
                },
              ]
            : []),
        ]}
        onDismiss={dismissBanner}
      />

      {studioLoading ? (
        <Spinner />
      ) : bootstrapError ? (
        <section style={S.card} aria-live="polite">
          <div style={S.contentTitle}>Studio data failed to load</div>
          <div style={{ ...S.errorBox, marginTop: 12, marginBottom: 0 }}>
            {bootstrapError.code}: {bootstrapError.message}
          </div>
          <button
            type="button"
            onClick={loadStudioData}
            style={{ ...btn('primary'), marginTop: 16 }}
          >
            Retry bootstrap
          </button>
        </section>
      ) : (
        <Suspense fallback={<Spinner />}>
          {tab === 'dashboard' && (
            <DashboardTab
              posts={posts}
              ideas={ideas}
              projects={projects}
              positions={positions}
              archivedPosts={archivedPosts}
              archivedIdeas={archivedIdeas}
              researchBriefs={researchBriefs}
              reports={reports}
              publishAttempts={publishAttempts}
              runtimeVerifications={runtimeVerifications}
              integrationHealth={integrationHealth}
              startDate={startDate}
              authorityReview={authorityReview}
              authorityReviews={authorityReviews}
              authorityTopicCandidates={authorityTopicCandidates}
              authorityCoverage={authorityCoverage}
              strategicAssets={strategicAssets}
              strategicAssetCoverage={strategicAssetCoverage}
              authorityReviewLoading={authorityReviewLoading}
              onRunAuthorityReview={handleAuthorityReview}
              onOpenResumeItem={(item) => {
                if (item.targetId) setFocusedPostId(item.targetId)
                setTab(item.actionTab)
              }}
              onOpenTab={setTab}
            />
          )}

          {tab === 'create' && (
            <CreateTab
              onSave={handleSave}
              onToast={setToast}
              posts={posts}
              prefillTopic={prefillTopic}
              onClearPrefill={() => setPrefillTopic('')}
              prefillCtx={prefillCtx}
              onClearPrefillCtx={() => setPrefillCtx('')}
              prefillPillar={prefillPillar}
              onClearPrefillPillar={() => setPrefillPillar('')}
              prefillContentType={prefillContentType}
              onClearPrefillContentType={() => setPrefillContentType('')}
              prefillMode={prefillMode}
              onClearPrefillMode={() => setPrefillMode('')}
              savedDraftId={prefillDraftId}
              composeLaunchContext={prefillComposeContext}
              prefillSourceWork={prefillSourceWork}
              onClearPrefillSourceWork={() => setPrefillSourceWork(null)}
              prefillTruthLinks={prefillTruthLinks}
              onClearPrefillTruthLinks={() => setPrefillTruthLinks(null)}
              projects={projects}
              positions={positions}
              strategicAssets={strategicAssets}
              authorityTopicCandidates={authorityTopicCandidates}
              onUpdateProofAsset={handleUpdateProofAsset}
              onRefreshProofAssets={refreshProofAssets}
              integrationHealth={integrationHealth}
            />
          )}

          {tab === 'ideas' && (
            <IdeasTab
              ideas={ideas}
              projects={projects}
              positions={positions}
              authorityReviews={authorityReviews}
              authorityTopicCandidates={authorityTopicCandidates}
              authorityCoverage={authorityCoverage}
              onSaveIdea={handleSaveIdea}
              onUpdateIdea={handleUpdateIdea}
              onDeleteIdea={handleDeleteIdea}
              onRunAuthorityReview={handleAuthorityReview}
              onUpdateAuthorityTopicCandidate={
                handleUpdateAuthorityTopicCandidate
              }
              onConvertAuthorityTopicCandidate={
                handleConvertAuthorityTopicCandidate
              }
              onOpenConvertedPost={handleOpenConvertedCapturePost}
              onNavigate={handleNavigateIdea}
              onToast={setToast}
              githubSettings={githubSettings}
              onGithubCapturesImported={handleGithubCapturesImported}
            />
          )}

          {tab === 'truth' && (
            <TruthTab
              projects={projects}
              positions={positions}
              truthSuggestions={truthSuggestions}
              truthReviewQueue={truthReviewQueue}
              ideas={ideas}
              posts={posts}
              researchBriefs={researchBriefs}
              onSaveProject={handleSaveProject}
              onUpdateProject={handleUpdateTruthProject}
              onSavePosition={handleSavePosition}
              onUpdatePosition={handleUpdateTruthPosition}
              onApproveSuggestion={handleApproveTruthSuggestion}
              onBatchResolveSuggestions={handleBatchResolveTruthSuggestions}
              onDismissSuggestion={handleDismissTruthSuggestion}
              onInferSuggestions={handleInferTruthSuggestions}
              onToast={setToast}
            />
          )}

          {tab === 'research' && (
            <ResearchTab
              onToast={setToast}
              integrationHealth={integrationHealth}
              researchBriefs={researchBriefs}
              projects={projects}
              positions={positions}
              strategicAssets={strategicAssets}
              onSaveBrief={handleSaveBrief}
              onUpdateBrief={handleUpdateBrief}
              onUpdateProofAsset={handleUpdateProofAsset}
              onCaptureBrief={handleCaptureResearchBrief}
            />
          )}

          {/* Wave 2.2 — Posts consolidation. The new `posts` nav entry
              defaults to the drafts slice; legacy `drafts`/`queue`/
              `delivered` IDs stay routable so deep links and Dashboard
              handoffs (Resume draft / Send to queue / Skip) keep
              working. The internal sub-tab strip in PostsTab calls
              `onChangeStatus` which we wire straight back to setTab. */}
          {['posts', 'queue', 'drafts', 'delivered'].includes(tab) && (
            <div>
              <PostsTab
                posts={posts}
                publishAttempts={publishAttempts}
                runtimeVerifications={runtimeVerifications}
                filterStatus={
                  tab === 'queue'
                    ? 'scheduled'
                    : tab === 'delivered'
                      ? 'published'
                      : 'draft'
                }
                onChangeStatus={(subId) => setTab(subId)}
                onDelete={handleDelete}
                onMarkPublished={handleMarkPublished}
                onEdit={handleEdit}
                onOpenCompose={handleOpenDraftInCompose}
                onUpdatePost={handleUpdatePost}
                onDuplicate={handleDuplicate}
                onRetrySync={handleRetrySync}
                onSubstackPush={substackPush}
                integrationHealth={integrationHealth}
                onToast={setToast}
                onOpenSettings={() => setTab('settings')}
                onOpenTab={setTab}
                selectedPostId={focusedPostId}
                onFocusedPostConsumed={() => setFocusedPostId('')}
                projects={projects}
                positions={positions}
              />
            </div>
          )}

          {tab === 'lancer' && (
            <LancerTab
              posts={posts}
              onOpenCompose={(post) =>
                handleOpenDraftInCompose(post, {
                  surface: 'lancer',
                  label: 'Lancer',
                })
              }
            />
          )}

          {tab === 'engagement' && <EngagementTab />}

          {tab === 'insights' && <InsightsTab onOpenTab={setTab} />}

          {tab === 'calendar' && <CalendarTab posts={posts} />}
          {tab === 'progress' && (
            <ProgressTab
              posts={posts}
              publishAttempts={publishAttempts}
              startDate={startDate}
              onStartDateChange={handleStartDateChange}
              onResetStartDate={handleResetStartDate}
            />
          )}
          {tab === 'reports' && (
            <ReportTab
              onToast={setToast}
              integrationHealth={integrationHealth}
              researchBriefs={researchBriefs}
              reports={reports}
              strategicAssets={strategicAssets}
              projects={projects}
              positions={positions}
              onUpdateProofAsset={handleUpdateProofAsset}
              onReportCreated={handleReportCreated}
            />
          )}
          {tab === 'proof' && (
            <ProofTab
              assets={strategicAssets}
              coverage={strategicAssetCoverage}
              projects={projects}
              positions={positions}
              onUpdateAsset={handleUpdateProofAsset}
              onCreateTopicCandidate={handleCreateTopicFromProofAsset}
              onCreateTopicCandidateAndOpen={
                handleCreateTopicFromProofAssetAndOpenCompose
              }
              onRefresh={refreshProofAssets}
              onToast={setToast}
            />
          )}
          {tab === 'archive' && (
            <ArchiveTab
              archivedPosts={archivedPosts}
              archivedIdeas={archivedIdeas}
              archiveLoading={
                archivedPostsCount > 0 && archivedPosts.length === 0
              }
              onRestorePost={handleRestorePost}
              onRestoreIdea={handleRestoreIdea}
              onToast={setToast}
            />
          )}
          {tab === 'autonomy' && <AutonomyTab />}
          {tab === 'settings' && (
            <SettingsTab
              posts={posts}
              archivedPosts={archivedPosts}
              ideas={ideas}
              archivedIdeas={archivedIdeas}
              startDate={startDate}
              integrationHealth={integrationHealth}
              runtimeVerifications={runtimeVerifications}
              githubSettings={githubSettings}
              migrationState={migrationState}
              legacySnapshot={legacySnapshot}
              onImportLegacyLocalData={handleImportLegacyLocalData}
              onImportStudioData={handleImportStudioData}
              onRestorePost={handleRestorePost}
              onRestoreIdea={handleRestoreIdea}
              onRunRuntimeVerification={handleRunRuntimeVerification}
              onGithubSettingsChange={handleGithubSettingsChange}
              onToast={setToast}
            />
          )}
        </Suspense>
      )}
      <Toast msg={toast} onDone={() => setToast('')} S={S} />
    </AppShell>
  )
}
